import React, { useState, useCallback, useEffect, useRef } from 'react';
import { Platform } from 'react-native';
import type { AlertButton, AlertOptions } from 'react-native';

import { NotificationContext, type NotificationContextType } from './context';
import { ModalNotification } from './modal';
import { RetryToastBridge } from './retry-toast-bridge';
import { ToastNotification } from './toast';

import { NotificationHistoryViewer, setNotificationHistoryOwner } from '@/services/notification-history';
import {
	type NotificationItem,
	type NotificationOptions,
	type NotificationAction,
	type NotificationVariant,
	determineNotificationType,
	determineNotificationVariant,
	convertAlertButtonsToActions,
	generateNotificationId,
	getPlatformDefaults,
	notificationQueue,
} from '@/utils/notification-manager';

export { useNotification, useOptionalNotification } from './context';

export const NotificationProvider: React.FC<{
	children: React.ReactNode;
	/**
	 * The signed-in user, who owns the persisted notification history. A PROP rather than read
	 * from the session here, so this provider stays free of the auth module (see use-composer-draft).
	 * Omitted (undefined) ⇒ this provider leaves the history alone.
	 */
	userId?: string | null;
}> = ({ children, userId }) => {
	const [notifications, setNotifications] = useState<NotificationItem[]>([]);
	const queueRef = useRef(notificationQueue);

	// Every identity change — sign-out included — leaves only the new user's history in storage.
	useEffect(() => {
		if (userId !== undefined) void setNotificationHistoryOwner(userId);
	}, [userId]);

	const updateNotifications = useCallback(() => {
		setNotifications(queueRef.current.getAll());
	}, []);

	const showToast = useCallback((
		message: string,
		options: NotificationOptions = {},
	): string => {
		const id = generateNotificationId();
		const platformDefaults = getPlatformDefaults();

		const notification: NotificationItem = {
			id,
			type: 'toast',
			message,
			variant: options.variant || 'info',
			actions: options.actions,
			options: {
				...platformDefaults,
				...options,
				duration: options.duration || platformDefaults.duration || 4000,
			},
			createdAt: Date.now(),
		};

		queueRef.current.add(notification);
		updateNotifications();

		if (notification.options.duration) {
			setTimeout(() => {
				dismiss(id);
			}, notification.options.duration);
		}

		return id;
	// eslint-disable-next-line react-hooks/exhaustive-deps -- dismiss is defined later with useCallback; circular dependency between showToast and dismiss
	}, [updateNotifications]);

	const showModal = useCallback(({
		title,
		variant,
		message,
		actions,
	}: {
		title: string,
		variant?: NotificationVariant,
		message?: string,
		actions?: NotificationAction[],
	}) => {
		const id = generateNotificationId();

		const notification: NotificationItem = {
			id,
			type: 'modal',
			title,
			message: message || '',
			variant: variant || 'info',
			actions: actions || [{ label: 'OK', onPress: () => dismiss(id), style: 'default' }],
			options: {
				dismissible: true,
				position: 'center',
			},
			createdAt: Date.now(),
		};

		queueRef.current.add(notification);
		updateNotifications();
	// eslint-disable-next-line react-hooks/exhaustive-deps -- dismiss is defined later with useCallback; circular dependency between showModal and dismiss
	}, [updateNotifications]);

	const showErrorModal = useCallback((
		error: any,
		title = 'Error',
	) => {
		showModal({
			title,
			message: error instanceof Error ? error.message : String(error),
			variant: 'error',
		});
	}, [showModal]);

	const showBanner = useCallback((
		message: string,
		variant: NotificationVariant = 'info',
	) => {
		const id = generateNotificationId();

		const notification: NotificationItem = {
			id,
			type: 'banner',
			message,
			variant,
			actions: [{ label: 'Dismiss', onPress: () => dismiss(id), style: 'default' }],
			options: {
				dismissible: true,
				position: 'top',
			},
			createdAt: Date.now(),
		};

		queueRef.current.add(notification);
		updateNotifications();
	// eslint-disable-next-line react-hooks/exhaustive-deps -- dismiss is defined later with useCallback; circular dependency between showBanner and dismiss
	}, [updateNotifications]);

	const alert = useCallback((
		title: string,
		message?: string,
		buttons?: AlertButton[],
		options?: AlertOptions,
	) => {
		if (Platform.OS !== 'web') {
			// Dynamic import avoids the circular dep between this provider and RN's Alert.
			import('react-native').then(({ Alert }) => {
				Alert.alert(title, message, buttons, options);
			});
			return;
		}

		const notificationType = determineNotificationType(title, message || '', buttons || []);
		const variant = determineNotificationVariant(title, message || '', notificationType);
		const actions = buttons ? convertAlertButtonsToActions(buttons) : undefined;

		switch (notificationType) {
			case 'toast':
				showToast(message || title, { variant, actions });
				break;
			case 'modal':
				showModal({ title, message, actions, variant });
				break;
			case 'banner':
				showBanner(message || title, variant);
				break;
		}
	}, [showToast, showModal, showBanner]);

	const dismiss = useCallback((id: string) => {
		queueRef.current.remove(id);
		updateNotifications();
	}, [updateNotifications]);

	const dismissAll = useCallback(() => {
		queueRef.current.clear();
		updateNotifications();
	}, [updateNotifications]);

	const getNotifications = useCallback(() => {
		return queueRef.current.getAll();
	}, []);

	const contextValue: NotificationContextType = {
		showToast,
		showModal,
		showErrorModal,
		showBanner,
		alert,
		dismiss,
		dismissAll,
		getNotifications,
		notifications,
	};

	return (
		<NotificationContext.Provider value={contextValue}>
			{/* The history only ever shows THIS user's entries, even before the owner effect runs. */}
			<NotificationHistoryViewer value={userId}>{children}</NotificationHistoryViewer>

			{/* Subscribes to apiService retry events and surfaces the "retrying..." toast */}
			<RetryToastBridge />

			{notifications.map((notification) => {
				switch (notification.type) {
					case 'toast':
						return (
							<ToastNotification
								key={notification.id}
								notification={notification}
								onDismiss={dismiss}
							/>
						);
					case 'modal':
						return (
							<ModalNotification
								key={notification.id}
								notification={notification}
								onDismiss={dismiss}
							/>
						);
					case 'banner':
						// TODO: Implement banner component in Phase 2
						return null;
					default:
						return null;
				}
			})}
		</NotificationContext.Provider>
	);
};
